import * as React from 'react';
import { useSelector } from 'react-redux';
import { useTranslations } from '../../../../store/translations/hooks';
import { TK } from '../../../../store/translations/translationKeys';
import { AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';
import { ColumnMapping } from '../../../Clients/ImportClients/autoMap';
import { getGeneralSettings } from '../../../../store/settings/selectors';
import { saveAppointmentMapping } from './useAppointmentMappingStorage';
import { APPOINTMENT_MULTI_COLUMN_FIELDS, AppointmentField } from './autoMapAppointments';
import { ImportAppointmentOptions } from './ImportAppointmentOptions';

interface FieldDef {
    id: AppointmentField;
    labelKey: TK;
    required: boolean;
}

const FIELD_DEFS: FieldDef[] = [
    { id: 'date',         labelKey: TK.date,         required: true  },
    { id: 'start',        labelKey: TK.startTime,    required: false },
    { id: 'end',          labelKey: TK.endTime,      required: false },
    { id: 'professional', labelKey: TK.professional, required: true  },
    { id: 'client',       labelKey: TK.client,       required: true  },
    { id: 'type',         labelKey: TK.service,      required: false },
    { id: 'room',         labelKey: TK.room,         required: false },
    { id: 'price',        labelKey: TK.price,        required: false },
    { id: 'notes',        labelKey: TK.notes,        required: false },
    { id: 'status',       labelKey: TK.importAppointmentsFieldStatus, required: false },
];

interface DragInfo {
    colIndex: number;
    fromField: string | null;
}

function applyAssign(
    mapping: ColumnMapping,
    colIndex: number,
    toField: string,
    fromField: string | null,
): ColumnMapping {
    const fieldMap: Record<string, number[]> = {};
    for (const [k, v] of Object.entries(mapping.fieldMap)) fieldMap[k] = [...v];
    let unmappedCols = [...mapping.unmappedCols];

    if (fromField !== null) {
        fieldMap[fromField] = (fieldMap[fromField] ?? []).filter(i => i !== colIndex);
        if (fieldMap[fromField].length === 0) delete fieldMap[fromField];
    } else {
        unmappedCols = unmappedCols.filter(i => i !== colIndex);
    }

    if (!APPOINTMENT_MULTI_COLUMN_FIELDS.has(toField)) {
        const evicted = fieldMap[toField] ?? [];
        unmappedCols = [...unmappedCols, ...evicted];
        delete fieldMap[toField];
    }

    fieldMap[toField] = [...(fieldMap[toField] ?? []), colIndex];
    return { fieldMap, unmappedCols };
}

function applyRemove(mapping: ColumnMapping, colIndex: number, fromField: string): ColumnMapping {
    const fieldMap: Record<string, number[]> = {};
    for (const [k, v] of Object.entries(mapping.fieldMap)) fieldMap[k] = [...v];
    fieldMap[fromField] = (fieldMap[fromField] ?? []).filter(i => i !== colIndex);
    if (fieldMap[fromField].length === 0) delete fieldMap[fromField];
    return { fieldMap, unmappedCols: [...mapping.unmappedCols, colIndex] };
}

interface Props {
    result: AnalysisResult;
    mapping: ColumnMapping;
    onMappingChange: (m: ColumnMapping) => void;
    options: ImportAppointmentOptions;
    onOptionsChange: (o: ImportAppointmentOptions) => void;
}

const AppointmentMappingStep: React.FC<Props> = ({
    result,
    mapping,
    onMappingChange,
    options,
    onOptionsChange,
}) => {
    const t = useTranslations();
    const generalSettings = useSelector(getGeneralSettings);
    const roomKeys = React.useMemo(() => Object.keys(generalSettings?.rooms ?? {}), [generalSettings]);
    const [dragOverTarget, setDragOverTarget] = React.useState<string | 'unmapped' | null>(null);

    React.useEffect(() => {
        saveAppointmentMapping(mapping.fieldMap, result.headers);
    }, [mapping, result.headers]);

    const handleDragStart = (e: React.DragEvent, colIndex: number, fromField: string | null) => {
        e.dataTransfer.setData('text/plain', JSON.stringify({ colIndex, fromField }));
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDropOnField = (e: React.DragEvent, toField: string) => {
        e.preventDefault();
        setDragOverTarget(null);
        try {
            const { colIndex, fromField }: DragInfo = JSON.parse(e.dataTransfer.getData('text/plain'));
            onMappingChange(applyAssign(mapping, colIndex, toField, fromField));
        } catch { /* ignore */ }
    };

    const handleDropOnUnmapped = (e: React.DragEvent) => {
        e.preventDefault();
        setDragOverTarget(null);
        try {
            const { colIndex, fromField }: DragInfo = JSON.parse(e.dataTransfer.getData('text/plain'));
            if (fromField !== null) onMappingChange(applyRemove(mapping, colIndex, fromField));
        } catch { /* ignore */ }
    };

    const handleDragEnter = (e: React.DragEvent, target: string | 'unmapped') => {
        e.preventDefault();
        setDragOverTarget(target);
    };

    const handleDragLeave = (e: React.DragEvent) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setDragOverTarget(null);
        }
    };

    const sampleRow = result.sampleRows[0] ?? [];

    const renderChip = (colIndex: number, fromField: string | null) => {
        const header = result.headers[colIndex] ?? `col${colIndex}`;
        const sample = sampleRow[colIndex];
        return (
            <span
                key={colIndex}
                draggable
                onDragStart={e => handleDragStart(e, colIndex, fromField)}
                title={sample ? `e.g. ${sample}` : header}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 'var(--spacer-XXS)',
                    background: 'white',
                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                    borderRadius: '12px',
                    padding: '2px var(--spacer-XS)',
                    fontSize: '0.8rem',
                    cursor: 'grab',
                    userSelect: 'none' as const,
                    whiteSpace: 'nowrap' as const,
                    maxWidth: '180px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                }}
            >
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{header}</span>
                {fromField !== null && (
                    <button
                        onClick={() => onMappingChange(applyRemove(mapping, colIndex, fromField))}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '0',
                            lineHeight: 1,
                            color: 'var(--text-muted)',
                            fontSize: '0.9rem',
                            flexShrink: 0,
                        }}
                    >
                        ×
                    </button>
                )}
            </span>
        );
    };

    return (
        <div style={{ display: 'flex', gap: 'var(--spacer-M)', height: '65vh' }}>
            {/* Left: field rows */}
            <div style={{ flex: 3, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {FIELD_DEFS.map(field => {
                    const assignedCols = mapping.fieldMap[field.id] ?? [];
                    const isOver = dragOverTarget === field.id;
                    const isMissing = field.required && assignedCols.length === 0;
                    return (
                        <div
                            key={field.id}
                            onDragOver={e => { e.preventDefault(); setDragOverTarget(field.id); }}
                            onDragEnter={e => handleDragEnter(e, field.id)}
                            onDragLeave={handleDragLeave}
                            onDrop={e => handleDropOnField(e, field.id)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacer-S)',
                                padding: 'var(--spacer-XS) var(--spacer-S)',
                                borderRadius: '6px',
                                border: `var(--border-thin) solid ${isOver ? 'var(--appBlue)' : isMissing ? 'var(--warning-color)' : 'var(--appLigthGrey)'}`,
                                background: isOver ? 'var(--bg-hover-light)' : isMissing ? 'var(--warning-bg)' : 'white',
                                minHeight: '38px',
                                transition: 'border-color 0.1s, background 0.1s',
                            }}
                        >
                            <span style={{
                                width: '140px',
                                flexShrink: 0,
                                fontSize: '0.8rem',
                                color: 'var(--text-muted)',
                                fontWeight: 600,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}>
                                {t(field.labelKey)}
                                {!field.required && (
                                    <span style={{ fontWeight: 400, marginLeft: 'var(--spacer-XXS)', color: 'var(--text-placeholder)' }}>
                                        *
                                    </span>
                                )}
                            </span>
                            <div style={{
                                flex: 1,
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 'var(--spacer-XXS)',
                                alignItems: 'center',
                                minWidth: 0,
                            }}>
                                {assignedCols.length > 0
                                    ? assignedCols.map(i => renderChip(i, field.id))
                                    : (
                                        <span style={{ fontSize: '0.75rem', color: 'var(--text-placeholder)', fontStyle: 'italic' }}>
                                            {t(TK.importAppointmentsDragHere)}
                                        </span>
                                    )
                                }
                            </div>
                        </div>
                    );
                })}
                <span style={{ fontSize: '0.72rem', color: 'var(--text-placeholder)', marginTop: 'var(--spacer-XS)' }}>
                    * optional
                </span>
            </div>

            {/* Right: options + unmapped */}
            <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)' }}>
                <div style={{
                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                    borderRadius: '6px',
                    background: 'white',
                    padding: 'var(--spacer-S)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--spacer-S)',
                }}>
                    <div style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: 'var(--text-muted)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                    }}>
                        {t(TK.options)}
                    </div>

                    <label style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 'var(--spacer-XS)',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        color: 'var(--text-body)',
                        lineHeight: 1.4,
                    }}>
                        <input
                            type="checkbox"
                            checked={options.createMissingClients}
                            onChange={e => onOptionsChange({ ...options, createMissingClients: e.target.checked })}
                            style={{ marginTop: '2px', flexShrink: 0, accentColor: 'var(--appBlue)' }}
                        />
                        <span>{t(TK.importAppointmentsCreateMissingClients)}</span>
                    </label>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)' }}>
                        <label style={{ fontSize: '0.85rem', color: 'var(--text-body)', fontWeight: 500 }}>
                            {t(TK.importAppointmentsDefaultDuration)}
                        </label>
                        <input
                            type="number"
                            min={1}
                            max={480}
                            value={options.defaultDurationMinutes}
                            onChange={e => {
                                const v = parseInt(e.target.value, 10);
                                if (!isNaN(v) && v > 0) onOptionsChange({ ...options, defaultDurationMinutes: v });
                            }}
                            style={{
                                fontSize: '0.85rem',
                                padding: '4px var(--spacer-XS)',
                                border: 'var(--border-thin) solid var(--appLigthGrey)',
                                borderRadius: '4px',
                                color: 'var(--text-body)',
                                background: 'white',
                                width: '80px',
                            }}
                        />
                    </div>

                    {roomKeys.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XXS)' }}>
                            <label style={{ fontSize: '0.85rem', color: 'var(--text-body)', fontWeight: 500 }}>
                                {t(TK.importAppointmentsDefaultRoom)}
                            </label>
                            <select
                                value={options.defaultRoom ?? ''}
                                onChange={e => onOptionsChange({ ...options, defaultRoom: e.target.value || undefined })}
                                style={{
                                    fontSize: '0.85rem',
                                    padding: '4px var(--spacer-XS)',
                                    border: 'var(--border-thin) solid var(--appLigthGrey)',
                                    borderRadius: '4px',
                                    color: 'var(--text-body)',
                                    background: 'white',
                                }}
                            >
                                <option value="">{t(TK.importAppointmentsNoRoom)}</option>
                                {roomKeys.map(key => (
                                    <option key={key} value={key}>{key}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                <div style={{
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: 'var(--text-muted)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                }}>
                    {t(TK.importAppointmentsUnmappedColumns)}
                    {mapping.unmappedCols.length > 0 && (
                        <span style={{ marginLeft: 'var(--spacer-XS)', fontWeight: 400 }}>
                            ({mapping.unmappedCols.length})
                        </span>
                    )}
                </div>
                <div
                    onDragOver={e => { e.preventDefault(); setDragOverTarget('unmapped'); }}
                    onDragEnter={e => handleDragEnter(e, 'unmapped')}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropOnUnmapped}
                    style={{
                        flex: 1,
                        overflowY: 'auto',
                        border: `var(--border-thin) dashed ${dragOverTarget === 'unmapped' ? 'var(--appBlue)' : 'var(--appLigthGrey)'}`,
                        background: dragOverTarget === 'unmapped' ? 'var(--bg-hover-light)' : 'var(--bg-light)',
                        borderRadius: '6px',
                        padding: 'var(--spacer-S)',
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignContent: 'flex-start',
                        gap: 'var(--spacer-XS)',
                        transition: 'border-color 0.1s, background 0.1s',
                    }}
                >
                    {mapping.unmappedCols.map(i => renderChip(i, null))}
                    {mapping.unmappedCols.length === 0 && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-placeholder)', fontStyle: 'italic' }}>
                            —
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AppointmentMappingStep;
